import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Brand, BrandingConfigurationService } from '@ptl/brand';
import { PtlWindowLocationService } from '@ptl/global-components';
import { NormalizedResponse } from '@ptl/xhr';
import { ServerDateTimeService } from 'src/app/angular-elements/shared/services/server-date-time.service';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ProcessedResultsInterface, EligibilityResponsePayload, EligibilityRenderable, ONXEligibilityResponseApplicant } from '../../interfaces/eligibility.interface';
import { EligibilityProcessorService } from '../../services/eligibility-processor.service';
// import * as moment from 'moment';
import moment from 'moment';
import { AbstractControl, FormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatRadioChange } from '@angular/material/radio';
import { EligibilityManagerService } from '../../services/eligibility-manager.service';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME, setCurrentProspectId } from '../../reducers/eligibility.reducer';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { Subscription } from 'rxjs';
import { plansNeedRefreshedFromBackend } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { ProspectService } from '../../services/prospect.service';
import { Prospect, UpdateProspectStatusRequest } from '../../interfaces/prospect.interface';
import { ProspectServiceParams } from '../../interfaces/prospect-query-params.interface';
import { FFMRedirectService } from '../../services/ffmredirect.service';

/** When Datapower intercepts responses from CMS we will get some of the below values:
 * 1) ProspectId - all cases
 * 2) Others - will show up in the case where LtpaToken2 is lost in transit (researched, unable to find root cause)
 *    code will then be required to use those values to manually update the prospect in the way that the consumers/response service kicked off in the past
 *    SUPER WORKAROUND
 */
interface DatapowerResponseQueryParams {
  prospectId: string;
  error?: string;
  message?: string;
  ExceptionReason?: string;
  FFEAssignedConsumerID?: string;
  FFEUserID?: string;
  InformationExchangeSystemID?: string;
  PartnerAssignedConsumerID?: string;
  StateExchangeCode?: string;
}
interface TobaccoFormControls {
  [applicantIndex: string]: UntypedFormGroup;
}
interface TobaccoFormControlsAsJson {
  [applicantIndex: string]: {
    isUser: 'Y' | 'N';
    lastUseDate: unknown;
  };
}

interface ApplicantFormControls {
  selectedApplicant: AbstractControl;
}
interface ApplicantFormControlsAsJson {
  selectedApplicant: string;
}

interface EffectiveDateFormControls {
  selectedEffectiveDate: AbstractControl;
}
interface EffectiveDateFormControlsAsJson {
  selectedEffectiveDate: string;
}

interface FFMParamsInterface {
  error: 'true' | any;
  prospectId: string;
}

@Component({
    selector: 'app-consumer-eligibility-status',
    templateUrl: './consumer-eligibility-status.component.html',
    styleUrls: ['./consumer-eligibility-status.component.scss'],
    standalone: false
})
export class ConsumerEligibilityStatusComponent implements OnInit, OnDestroy {

  @Input() enableOnxSales: 'true' | 'false';
  get _enableOnxSales() { return this.enableOnxSales === 'true'; }
  @Input() selectedMarket: LINE_OF_BUSINESS_ENUM;

  @Input() viewPlansUrl: string;

  processedResults: EligibilityRenderable;
  applicantContainerID: string;

  tobaccoForm: UntypedFormGroup;
  tobaccoFormSubscription: Subscription;
  applicantForm: UntypedFormGroup;
  applicantFormSubscription: Subscription;
  applicantContainer: { label: string, value: string, selected: boolean }[] = [];
  effectiveDateForm: UntypedFormGroup;
  effectiveDateContainer: { label: string, value: string, selected: boolean }[] = [];

  prospectId: string;

  /** Tracking expand/collapse of more-info drawers */
  eligibleApplicantDrawers = {};

  constructor(
    private windowLocationService: PtlWindowLocationService,
    private commonUtilityService: CommonUtilityService,
    private eligibilityManagerService: EligibilityManagerService,
    private eligibilityProcessorService: EligibilityProcessorService,
    private serverDateTimeService: ServerDateTimeService,
    private brandService: BrandingConfigurationService,
    private fb: UntypedFormBuilder,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private prospectService: ProspectService,
    private ffmRedirectService: FFMRedirectService
  ) { }

  ngOnDestroy() {
    if (this.tobaccoFormSubscription) { this.tobaccoFormSubscription.unsubscribe(); }
    if (this.applicantFormSubscription) { this.applicantFormSubscription.unsubscribe(); }
  }

  /** FORM HELPERS */
  get tobaccoFormControls(): TobaccoFormControls {
    return this.tobaccoForm.controls as unknown as TobaccoFormControls;
  }
  get tobaccoFormControlsAsJson(): TobaccoFormControlsAsJson {
    return this.tobaccoForm.value as unknown as TobaccoFormControlsAsJson;
  }
  get applicantFormControls(): ApplicantFormControls {
    return this.applicantForm.controls as unknown as ApplicantFormControls;
  }
  get applicantFormControlsAsJson(): ApplicantFormControlsAsJson {
    return this.applicantForm.value as unknown as ApplicantFormControlsAsJson;
  }
  get effectiveDateFormControls(): EffectiveDateFormControls {
    return this.effectiveDateForm.controls as unknown as EffectiveDateFormControls;
  }
  get effectiveDateFormControlsAsJson(): EffectiveDateFormControlsAsJson {
    return this.effectiveDateForm.value as unknown as EffectiveDateFormControlsAsJson;
  }
  /** FORM HELPERS END */

  async ngOnInit(): Promise<void> {

    /** ProspectId controls access to stuff */
    let onExchangeEligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const queryParams: DatapowerResponseQueryParams = this.windowLocationService.getParsedQueryParams<{prospectId: string}>();

    /**
     * Given a prospect ID, fetch eligibility by setting it as the selected prospect ID
     * If we arrive to this page, given a defined error, manually update the prospect status and refresh with appropriate query params
     */
    if (queryParams?.prospectId) {

      /** LtpaToken2 dropped error - initiate manual update of prospect after completing application on CMS */
      if (queryParams.error && queryParams.message) {

        const onexConfigData = await this.ffmRedirectService.getONXConfig();

        const userInfo: UserInfoStateStructure = this.reducerUtilityService.getApplicationStates()[LOGIN_INFO_REDUCER_NAME];
        const prospectServiceParams: ProspectServiceParams = {
          ExceptionReason: queryParams.ExceptionReason || null,
          FFEAssignedConsumerID: queryParams.FFEAssignedConsumerID || null,
          FFEUserID: queryParams.FFEUserID || null,
          InformationExchangeSystemID: onexConfigData.Information_exchange_system_id || null,
          PartnerAssignedConsumerID: queryParams.prospectId || null,
          StateExchangeCode: queryParams.StateExchangeCode || null,
          TransferType: 'Completed eligibility application',
          UserType: this.commonUtilityService.isAgent() ? 'Agent' : 'Consumer'
        };

        // Clean NULLs
        Object.keys(prospectServiceParams).forEach(k => {
          if (!prospectServiceParams[k]) {
            delete prospectServiceParams[k];
          }
        });

        await this.prospectService.individualUpdateOnExchangeStatus(prospectServiceParams);
        this.windowLocationService.goToUrl(`${window.location.pathname}?prospectId=${queryParams.prospectId}`);
      }
      this.ngRedux.dispatch(setCurrentProspectId(queryParams.prospectId));
      onExchangeEligibilityState
        = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    }

    this.prospectId = onExchangeEligibilityState.currentProspectId;

    /** Eligibilty Results - either bare minimum for rendering errors or meaningful data from the service */
    let processedResults = await this.processTransaction();
    if (!processedResults) {
      processedResults = this.handleErrorApplication();
    }

    /** Flag producer */
    processedResults.isProducer = this.commonUtilityService.isAgent();

    this.processedResults = processedResults;

    this.postProcessChecked();
  }

  get eligibilityContent() {
    return this.eligibilityProcessorService.getEligibilityContent();
  }

  trackByIndex(index: number, person: ONXEligibilityResponseApplicant): number {
    return index;
  }

  postProcessChecked() {
    this.applicantContainer = []; // primarySelectorForm - options

    if (this.processedResults.primaryApplicants) {

      this.processedResults.primaryApplicants.forEach(a => {

        if (this.applicantContainerID && this.applicantContainerID === a.personID) {
          this.applicantContainer.push({ label: a.fullName.firstName, value: a.personID, selected: true });
        } else {
          this.applicantContainer.push({ label: a.fullName.firstName, value: a.personID, selected: false });
        }

      });

      this.buildApplicantForm(this.applicantContainer);
      this.buildTobaccoForm(this.processedResults.processedEligibilityResults.eligible);
      this.buildEffectiveDateForm(this.processedResults);
    }

  }

  tobaccoUsageChanged(applicantPersonId: string, event: MatRadioChange) {
    for (const person of this.processedResults.applicants) {
      if (person.personID === applicantPersonId) {
        console.log('hit the correct filter, tobaccoUsageChanged');
        person.tobaccoInd = event.value;

        if (['N', false, null].includes(person.tobaccoInd)) {
          delete person.tobaccoDate;
        }
      }
    }
  }
  tobaccoDateChanged(type: string, event: MatDatepickerInputEvent<Date>, applicantPersonId: string) {
    for (const person of this.processedResults.applicants) {
      if (person.personID === applicantPersonId) {
        console.log('hit the correct filter, tobaccoDateChanged');
        person.tobaccoDate = moment(event.value).format('MM/DD/YYYY');
      }
    }
  }

  async submit() {

    /** Is this really needed? */
    const objectCleanUp = (dataObject: EligibilityRenderable) => {
      // Delete all the data that doesn't need to be passed to view plans
      delete dataObject.messageStatus;
      // delete dataObject.messageTitle;
      // delete dataObject.messageContent;
      delete dataObject.messages;
      delete dataObject.showPageContent;
      delete dataObject.currentDate;
      delete dataObject.errorApp;
      delete dataObject.incompleteApp;
      delete dataObject.primaryApplicants;
    };

    /** This seems to just convert values into payload values */
    const populateDataObject = (dataObject: EligibilityRenderable): EligibilityRenderable => {

      const primaryID = this.applicantForm.controls['selectedApplicant'].value;

      dataObject.applicants.forEach(applicant => {

        if (primaryID === applicant.personID) {
          applicant.isPrimary = true;
        }

        if (
          this.tobaccoFormControlsAsJson[applicant.personID]?.isUser === null ||
          this.tobaccoFormControlsAsJson[applicant.personID]?.isUser === undefined
        ) {
          applicant.tobaccoInd = 'N';
        }

      });
      dataObject.effectiveDate = this.processedResults.applicants[0].effectiveDate;

      return dataObject;
    };

    const formValidators = (() => {

      return (
        this.tobaccoForm.valid &&
        this.applicantForm.valid &&
        this.effectiveDateForm.valid &&
        !(this.processedResults.QSEHRAorICHRA === true)
      );

    })();

    if (formValidators) {
      /** Is this line really needed, are either of them? TBD */
      // objectCleanUp(this.processedResults);
      this.processedResults = populateDataObject(this.processedResults);

      this.eligibilityManagerService.set(this.processedResults, this.prospectId);

      /*
       * In legacy, there's a line here regarding setting of the brands,
       * I don't think we need this since branding should be handled better in AEM
       */

      await this.prospectService.configureBrandingFromProspectResponse(this.processedResults.primaryApplicants[0].homeAddress);
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
      this.windowLocationService.goToUrl(this.viewPlansUrl);

    }
  }

  isAdult(now: Date, applicant: ONXEligibilityResponseApplicant) {
    const currentBrand: Brand = this.brandService.getCurrentBrand();

    /** @TODO update with dateOffset from dateService */
    if (
      currentBrand.individualMarketConstants.ageOfAdulthood < moment(now).diff(moment(applicant.dateOfBirth), 'year')
    ) {
      return true;
    }
    return false;
  }

  /** ProcessedResults is extended by the EligibilityRenderable */
  async processTransaction(): Promise<EligibilityRenderable> {

    const ffmParams: FFMParamsInterface = this.windowLocationService.getParsedQueryParams();
    if (ffmParams.error === 'true') {
      const processedResults: ProcessedResultsInterface = {};
      processedResults.messageStatus = 'errorAppDP';
      processedResults.errorAppDP = true;
      processedResults.messages = this.eligibilityContent['errorAppDP'];
      processedResults.messages.content = this.eligibilityContent.content;
      processedResults.showPageContent = this.contentDisplayCheck(processedResults);
      return processedResults as EligibilityRenderable;
    } else {
      return this.fetchEligibility();
    }
  }

  /** Determines if errors, derived from the query params, exist */
  contentDisplayCheck(data: ProcessedResultsInterface): boolean {
    // If the page has any of these error situations, don't display the content.
    if (data.multiTaxHouse || data.incompleteApp || data.errorApp || data.errorAppDP || data.contingency) {
      return false;
    } else {
      return true;
    }
  }

  // Sets default values for rendering the view with an application that is incomplete
  handleIncompleteApplication(): EligibilityRenderable {

    const processedResults: ProcessedResultsInterface = {};
    processedResults.messageStatus = 'incompleteApp';
    processedResults.incompleteApp = true;
    processedResults.messages = this.eligibilityContent[processedResults.messageStatus];
    processedResults.statusButtons = this.eligibilityContent.statusButtons;
    processedResults.messages.content = this.eligibilityContent.content;
    processedResults.showPageContent = this.contentDisplayCheck(processedResults);
    return processedResults as EligibilityRenderable;
  }

  // Sets default values for rendering the view with an application that is incomplete
  handleInconsistentBranding(): EligibilityRenderable {
    const processedResults: ProcessedResultsInterface = {};

    if (this.commonUtilityService.isAgent()) {
      processedResults.messageStatus = 'inconsistentBrandProdcuer';
    } else {
      processedResults.messageStatus = 'inconsistentBrandConsumer';
    }

    processedResults.incompleteApp = true;
    processedResults.messages = this.eligibilityContent[processedResults.messageStatus];
    processedResults.statusButtons = this.eligibilityContent.statusButtons;
    processedResults.messages.content = this.eligibilityContent.content;
    processedResults.showPageContent = this.contentDisplayCheck(processedResults);
    return processedResults as EligibilityRenderable;
  }

  // Sets default values for rendering the view with an application that is error-ed on the fetchEligibility service
  handleErrorApplication(): EligibilityRenderable {
    const processedResults: ProcessedResultsInterface = {};
    processedResults.messageStatus = 'errorApp';
    processedResults.errorApp = true;
    processedResults.messages = this.eligibilityContent[processedResults.messageStatus];
    processedResults.statusButtons = this.eligibilityContent.statusButtons;
    processedResults.messages.content = this.eligibilityContent.content;
    processedResults.showPageContent = this.contentDisplayCheck(processedResults);
    return processedResults as EligibilityRenderable;
  }

  processResults(data: EligibilityRenderable): EligibilityRenderable {
    if (
      data.applicationStatus === 'New Customer' ||
      data.applicationStatus === 'Eligibility Exception' ||
      data.applicationStatus === 'User Initiated Return'
    ) {
      // This is for the case that the application was not completed on FFM or previously
      // Instead of doing the next call, we populate the error messages and display them.
      return this.handleIncompleteApplication();
    } else if (data.applicationStatus === 'Inconsistent Regional Branding') {
      return this.handleInconsistentBranding();
    } else {
      return this.eligibilityProcessorService.processEligibility(data);
    }
  }

  async fetchEligibility(): Promise<EligibilityRenderable> {

    const eligibilityData: NormalizedResponse<EligibilityResponsePayload>
      = await this.eligibilityProcessorService.fetchEligibility(this.prospectId);

    let eligibilityRenderable: EligibilityRenderable;

    if (!(eligibilityData instanceof HttpErrorResponse)) {

      if (eligibilityData.payload.applicants) {
        /** disable enrollment for producers if turned off */
        if (this.commonUtilityService.isAgent()) {
          if (!this._enableOnxSales) {
            eligibilityRenderable = Object.assign(
              {},
              eligibilityData.payload,
              { prospectId: this.prospectId },
              { payload: { messageStatus: 'contingency', contingency: true } }
            );

            return eligibilityRenderable;
          } else {
            eligibilityRenderable = Object.assign({}, eligibilityData.payload);
          }
        } else {
          eligibilityRenderable = Object.assign({}, eligibilityData.payload);
        }

        // Process all applicants
        const processedResults = this.processResults(eligibilityRenderable);

        // Add messaging based on processed applicants
        processedResults.messages = this.eligibilityContent[processedResults.messageStatus];
        processedResults.statusButtons = this.eligibilityContent.statusButtons;
        processedResults.messages.content = this.eligibilityContent.content;
        // In the case of Multi Taxed Household, Incomplete applications, or if they are ineligible for QHP at the household level
        // then do not build the main content of the page. Otherwise, build it.
        processedResults.showPageContent
          = this.contentDisplayCheck(processedResults);

        const currentDateResponse = await this.serverDateTimeService.getServerDateTime();
        // Get current server time and render template
        processedResults.currentDate = new Date(currentDateResponse.payload.currentCustomDateTime);

        return processedResults;
      }
    }

    return null;
  }

  /** Build the form, populate as appropriate */
  buildApplicantForm(
    applicantOptions: {
      label: string,
      value: string,
      selected: boolean
    }[]
  ) {

    this.applicantForm = this.fb.group({
      selectedApplicant: this.fb.control('', Validators.required)
    });

    if (applicantOptions.length > 0) {
      if (applicantOptions.length === 1) {

        /** Only 1 option */
        this.applicantFormControls.selectedApplicant.setValue(applicantOptions[0].value);

      } else {

        /** Select whatever applicant has been designated as pre-selected ( if multiples ) */
        applicantOptions.forEach(o => {
          if (o.selected) {
            this.applicantFormControls.selectedApplicant.setValue(o.value);
          }
        });

      }
    }

    this.applicantForm.updateValueAndValidity();

  }

  buildEffectiveDateForm(data: EligibilityRenderable) {
    this.effectiveDateContainer = [];
    this.effectiveDateContainer.push({
      label: data.applicants[0].effectiveDate,
      value: data.applicants[0].effectiveDate,
      selected: true
    });

    /** Select whatever applicant effective date has been designated as pre-selected ( if multiples ) */
    if (this.effectiveDateContainer.length > 1) {
      this.effectiveDateForm = this.fb.group({});
      this.effectiveDateContainer.forEach(o => {
        if (o.selected) {
          this.effectiveDateForm.addControl(
            'selectedEffectiveDate',
            this.fb.control(o.value, Validators.required)
          );
        }
      });
    } else {
      /** If only 1 effective date exists, select it */
      this.effectiveDateForm = this.fb.group({
        selectedEffectiveDate: this.fb.control(this.effectiveDateContainer[0].value, Validators.required)
      });
    }
  }

  /** Build the form, populate with ADULT applicants, use the index shared by this section and the template to identify */
  buildTobaccoForm(applicants: ONXEligibilityResponseApplicant[]): void {
    if (!this.tobaccoForm) {
      this.tobaccoForm = this.fb.group({});
    }

    applicants.forEach((a, i) => {
      if (this.isAdult(this.processedResults.currentDate, a)) {
        this.tobaccoForm.addControl(
          a.personID,
          this.fb.group({
            isUser: this.fb.control(null, Validators.required),
            lastUseDate: this.fb.control(null)
          })
        );
      } else {
        this.tobaccoForm.addControl(
          a.personID,
          this.fb.group({
            isUser: this.fb.control(false, Validators.required),
            lastUseDate: this.fb.control(null)
          })
        );
        this.tobaccoForm.controls[a.personID].disable();
      }

    });

    /** If tobacco used, date is needed; if tobacco is not used, date is unneeded */
    this.tobaccoFormSubscription = this.tobaccoForm.valueChanges.subscribe((v: TobaccoFormControlsAsJson) => {

      Object.keys(v).forEach(key => {
        const lastUseDateControl = (this.tobaccoFormControls[key] as UntypedFormGroup).controls['lastUseDate'];
        if (v[key].isUser === 'Y') {
          lastUseDateControl.setValidators([Validators.required, this.tobaccoLastUseDateValidator()]);
        } else {
          lastUseDateControl.setValue(null, { emitEvent: false });
          lastUseDateControl.clearValidators();
        }
        lastUseDateControl.updateValueAndValidity({ emitEvent: false });
      });


    });
  }

  /** Tobacco use should be within the last 60 days of NOW */
  tobaccoLastUseDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const now = moment();
      const lastTobaccoUsedDate = control.value;
      if (
        !(moment(now).diff(moment(lastTobaccoUsedDate), 'day') > 60) &&
        !(moment(now).diff(moment(lastTobaccoUsedDate), 'day') <= 0)
      ) {
        return null;
      } else {
        return { dateTooOldOrTooFuturistic: true };
      }

    };
  }

  goToMarketplace() {
    throw new Error('Not Implemented'); // uses ffmRedirectUtility to generate and SSO to send the user back
  }
}
